import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const EngineeringServicesPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="relative h-80 flex items-center justify-center overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Engineering Services</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            End-to-end engineering support from concept to commissioning
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Engineering Services for <span className="text-red-600">Oil & Gas Projects</span>
            </h2>
            <div className="w-24 h-1 bg-red-600 mb-8" />
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Engineering services in the oil & gas sector require precision, compliance with international standards, and seamless coordination across disciplines. Total Vision Oil and Gas Fields Services and Consultancy LLC SPC delivers end to end engineering support across upstream, midstream, and downstream facilities, ensuring safe, efficient, and cost effective project execution from concept to commissioning.
            </p>
          </div>

          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Multidisciplinary Engineering Capabilities</h3>
            
            <div className="mb-12">
              <h4 className="text-xl font-bold text-slate-900 mb-4">Process Engineering</h4>
              <p className="text-slate-700 mb-4">Process engineering forms the backbone of oil & gas facility design.</p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Development of PFDs, P&IDs, and heat & material balances',
                  'Process simulations for steady state and dynamic conditions',
                  'Equipment sizing, relief system design, and process safety analysis',
                  'HAZOP, HAZID, SIL studies and action close out',
                  'Optimization of existing facilities for improved throughput and energy efficiency'
                ].map((item, idx) => (
                  <Card key={idx} className="border-none shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-base">{item}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div className="mb-12">
              <h4 className="text-xl font-bold text-slate-900 mb-4">Mechanical and Piping Engineering</h4>
              <p className="text-slate-700 mb-4">Mechanical and piping systems must withstand demanding operating conditions.</p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Design of pressure vessels, heat exchangers, tanks, and rotating equipment',
                  'Piping layout, isometrics, and material specifications',
                  'Stress analysis, flexibility studies, and pipe support design'
                ].map((item, idx) => (
                  <Card key={idx} className="border-none shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-base">{item}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div className="mb-12">
              <h4 className="text-xl font-bold text-slate-900 mb-4">Electrical and Instrumentation Engineering</h4>
              <p className="text-slate-700 mb-4">Electrical and instrumentation systems are critical for safe and automated operations.</p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Electrical load lists, SLDs, cable routing, and grounding systems',
                  'Hazardous area classification and equipment selection',
                  'Instrument index, control narratives, loop diagrams, and logic development',
                  'Integration of DCS, ESD, F&G, and SCADA systems'
                ].map((item, idx) => (
                  <Card key={idx} className="border-none shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-base">{item}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div className="mb-12">
              <h4 className="text-xl font-bold text-slate-900 mb-4">Civil and Structural Engineering</h4>
              <p className="text-slate-700 mb-4">Civil and structural engineering ensures the physical integrity of oil & gas facilities.</p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Foundation design for static and rotating equipment',
                  'Structural analysis for pipe racks, platforms, and buildings',
                  'Geotechnical assessments and site development planning',
                  'Blast resistant and fire proofing design for high risk areas'
                ].map((item, idx) => (
                  <Card key={idx} className="border-none shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-base">{item}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Engineering Support Across the Project Lifecycle</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { title: 'Pre Award and FEED Support', items: ['Scope definition and technical clarifications', 'Conceptual and FEED level engineering', 'Cost estimation, MTOs, and technical bid evaluations', 'Risk assessment and value engineering'] },
                { title: 'Detailed Engineering and Design Development', items: ['3D modeling (E3D/SP3D/PDMS) and clash detection', 'Preparation of IFC deliverables', 'Vendor document review and technical coordination', 'Engineering quality assurance and compliance checks'] },
                { title: 'Procurement and Vendor Engineering', items: ['Preparation of technical requisitions and evaluation reports', 'Review of vendor drawings, data sheets, and compliance statements', 'FAT participation and inspection support', 'Expediting to maintain project schedules'] },
                { title: 'Construction and Site Engineering Support', items: ['Response to technical queries (TQs) and site clarifications', 'Review of construction drawings and method statements', 'As built documentation and red mark updates', 'Support for pre commissioning and commissioning activities'] }
              ].map((section, idx) => (
                <Card key={idx} className="border-none shadow-xl">
                  <CardContent className="p-8">
                    <h4 className="text-xl font-bold text-slate-900 mb-4">{section.title}</h4>
                    <ul className="space-y-3">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-slate-900 mb-6">Commitment to Quality, Safety, and Compliance</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Total Vision Oil and Gas Fields Services and Consultancy LLC SPC adheres to international standards including ISO, and regional regulatory frameworks. The company's engineering services emphasize:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {['Technical integrity', 'Operational safety', 'Cost efficiency', 'Environmental responsibility', 'Timely project delivery'].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EngineeringServicesPage;